import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiUsers, FiCheckSquare, FiTrendingUp, FiCalendar, FiEdit3, FiFileText, FiChevronRight } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import styles from './InstructorDashboard.module.css';

function InstructorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    coursesTaught: 0,
    totalStudents: 0,
    attendanceSessions: 0,
    averageAttendance: 0
  });

  const [taughtCourses, setTaughtCourses] = useState([]);
  const [showInactiveCourses, setShowInactiveCourses] = useState(false);

  const fetchInstructorData = useCallback(async () => {
    try {
      const [statsResponse, coursesResponse] = await Promise.all([
        apiService.getInstructorStats(),
        apiService.getCourses()
      ]);

      if (statsResponse.success && statsResponse.stats) {
        setStats(statsResponse.stats);
      }

      if (coursesResponse.success && coursesResponse.courses) {
        // Filter only courses taught by this instructor
        const taught = coursesResponse.courses.filter(course => course.instructor_id === user.id);
        setTaughtCourses(taught);
      }
    } catch (error) {
      logger.error('Error fetching instructor data', { error });
      setStats({
        coursesTaught: 0,
        totalStudents: 0,
        attendanceSessions: 0,
        averageAttendance: 0
      });
      setTaughtCourses([]);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchInstructorData();
    }
  }, [user, fetchInstructorData]);

  // Ensure page starts at top on mobile when component mounts
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, []);

  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleCreateSession = () => {
    navigate('/admin/attendance');
  };

  const handleManageStudents = () => {
    navigate('/admin/courses');
  };

  const quickActions = [
    {
      id: 'attendance',
      title: 'Attendance',
      description: 'Set up a new attendance session',
      icon: FiCheckSquare,
      onClick: handleCreateSession
    },
    {
      id: 'students',
      title: 'Students',
      description: 'View enrolled students and assign students',
      icon: FiUsers,
      onClick: handleManageStudents
    },
    {
      id: 'reports',
      title: 'Reports',
      description: 'View attendance statistics',
      icon: FiCalendar,
      onClick: () => navigate('/admin/attendance')
    },
    {
      id: 'content',
      title: 'Content',
      description: 'Modify course content and settings',
      icon: FiEdit3,
      onClick: () => navigate('/admin/courses')
    },
    {
      id: 'assignments',
      title: 'Assignments',
      description: 'Create and manage course assignments',
      icon: FiFileText,
      onClick: () => navigate('/assignments')
    },
    {
      id: 'quizzes',
      title: 'Quizzes',
      description: 'Create quizzes and view submissions',
      icon: FiEdit3,
      onClick: () => navigate('/instructor/quizzes')
    }
  ];

  const getStatIconClass = (color) => {
    switch (color) {
      case 'primary': return styles.statIconPrimary;
      case 'success': return styles.statIconSuccess;
      case 'warning': return styles.statIconWarning;
      case 'accent': return styles.statIconAccent;
      default: return styles.statIconPrimary;
    }
  };

  // Filter courses - show active by default, include inactive when toggle is on
  const filteredCourses = useMemo(() => {
    return taughtCourses.filter(course => {
      const isActive = course.is_active === 1 || course.is_active === true;
      if (showInactiveCourses) {
        // Show all courses (active and inactive)
        return true;
      }
      // Show only active courses
      return isActive;
    });
  }, [taughtCourses, showInactiveCourses]);

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerTitle}>
            <span className={styles.greetingArabic} dir="rtl">السلام عليكم</span>
            <span className={styles.greetingName}>, {user?.first_name || 'Instructor'}</span>
          </div>
          <p className={styles.headerSubtitle}>Manage your courses and track student progress.</p>
        </div>
        <div className={styles.headerAction}>
          <button 
            className={styles.manageCoursesButton}
            onClick={() => navigate('/admin/courses')}
          >
            Manage All Courses
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <motion.div
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('primary')}`}>
            <FiBookOpen size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.coursesTaught) || 0}</div>
            <div className={styles.statLabel}>COURSES TAUGHT</div>
          </div>
        </motion.div>

        <motion.div
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('success')}`}>
            <FiUsers size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.totalStudents) || 0}</div>
            <div className={styles.statLabel}>TOTAL STUDENTS</div>
          </div>
        </motion.div>

        <motion.div
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('warning')}`}>
            <FiCheckSquare size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.attendanceSessions) || 0}</div>
            <div className={styles.statLabel}>ATTENDANCE SESSIONS</div>
          </div>
        </motion.div>

        <motion.div
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('accent')}`}>
            <FiTrendingUp size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.averageAttendance) || 0}%</div>
            <div className={styles.statLabel}>AVG ATTENDANCE</div>
          </div>
        </motion.div>
      </div>

      {/* Main Content Grid */}
      <div className={styles.contentGrid}>
        {/* My Courses Section */}
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>My Courses</h2>
            {taughtCourses.some(course => !(course.is_active === 1 || course.is_active === true)) && (
              <button
                type="button"
                className={styles.toggleInactiveButton}
                onClick={() => setShowInactiveCourses(!showInactiveCourses)}
              >
                {showInactiveCourses ? 'Hide' : 'Show'} Previous Courses
              </button>
            )}
          </div>
          <div className={styles.coursesList}>
            {filteredCourses.length > 0 ? (
              filteredCourses.map((course) => (
                <motion.div
                  key={course.id}
                  className={styles.courseRow}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={styles.courseInfo}>
                    <div className={styles.courseTitleRow}>
                      <span className={styles.courseCode}>{course.code}</span>
                      <div className={styles.courseName}>{course.name}</div>
                    </div>
                    <div className={styles.courseStats}>
                      <span className={styles.courseStat}>
                        {course.enrolled_students || 0} Students
                      </span>
                      <span className={styles.courseStat}>
                        {course.session_count || 0} Sessions
                      </span>
                    </div>
                    <button
                      className={styles.courseManageButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCourseClick(course.id);
                      }}
                    >
                      <FiBookOpen size={14} />
                      Manage Course
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <FiBookOpen />
                <p>
                  {taughtCourses.length > 0 
                    ? showInactiveCourses
                      ? 'No courses found.'
                      : 'No active courses found.'
                    : 'No courses assigned yet.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Quick Actions</h2>
          </div>
          <div className={styles.quickActions}>
            <div className={styles.actionGrid}>
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <motion.div
                    key={action.id}
                    className={styles.actionItem}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={action.onClick}
                  >
                    <div className={styles.actionIcon}>
                      <IconComponent size={18} />
                    </div>
                    <div className={styles.actionContent}>
                      <div className={styles.actionTitle}>{action.title}</div>
                      <div className={styles.actionDescription}>{action.description}</div>
                    </div>
                    <FiChevronRight className={styles.actionChevron} size={18} />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InstructorDashboard;
